import { useState, useEffect } from 'react';
import { supabase, getOrCreateUser } from '../services/supabase';
import { getCurrentUser, isAuthenticated } from '../services/authUtils';

export const useAuth = () => {
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    const initAuth = async () => {
      try {
        console.log('[AUTH] Initializing auth...');
        
        // Check if Supabase client is initialized
        if (!supabase) {
          console.warn('[AUTH] Supabase client not initialized, skipping auth check');
          if (isMounted) {
            setLoading(false);
          }
          return;
        }
        
        // Get session with a reasonable timeout - but don't block the UI
        // The auth state change listener will handle updates, this is just for initial state
        let session = null;
        try {
          const sessionPromise = supabase.auth.getSession();
          const timeoutPromise = new Promise((resolve) => 
            setTimeout(() => resolve({ data: { session: null }, error: null }), 1500)
          );
          
          const result = await Promise.race([sessionPromise, timeoutPromise]);
          session = result.data?.session || null;
          
          if (result.error) {
            console.error('[AUTH] Error getting session:', result.error);
          }
        } catch (error) {
          console.warn('[AUTH] Session check failed or timed out:', error);
          // Continue with no session - auth state listener will update if session exists
        }
        
        console.log('[AUTH] Session check result:', session ? `User: ${session.user?.email}` : 'No session');
        
        // If we got a session, set it up
        if (session?.user && isMounted) {
          // Verify session is still valid (not expired)
          if (session.expires_at) {
            const expiresAt = new Date(session.expires_at * 1000);
            const now = new Date();
            if (expiresAt < now) {
              console.log('[AUTH] Session expired, clearing...');
              await supabase.auth.signOut();
              if (isMounted) {
                setUserId(null);
                setUser(null);
              }
              setLoading(false);
              return;
            }
          }
          
          const currentUser = session.user;
          console.log('[AUTH] Valid session found for user:', currentUser.email);
          
          // Set cookie for server-side auth check immediately
          if (session.access_token) {
            try {
              const expires = new Date(session.expires_at * 1000).toUTCString();
              document.cookie = `sb-access-token=${session.access_token}; expires=${expires}; path=/; SameSite=Lax; Secure`;
              console.log('[AUTH] Auth cookie set');
            } catch (cookieError) {
              console.warn('[AUTH] Error setting cookie:', cookieError);
            }
          }
          
          // Set user state immediately
          if (isMounted) {
            setUserId(currentUser.id);
            setUser(currentUser);
            console.log('[AUTH] User state set');
          }
          
          // Ensure user exists in users table (non-blocking - do this in background)
          getOrCreateUser(currentUser.id).catch((userError) => {
            console.warn('[AUTH] Error creating/getting user (non-blocking):', userError);
          });
        } else if (isMounted) {
          console.log('[AUTH] No valid session, user not logged in');
          // Explicitly clear state if no session
          setUserId(null);
          setUser(null);
        }
        // If no session, userId and user remain null - user can login/signup
        // Note: Auth state change listener will update state if session exists
      } catch (error) {
        console.error('[AUTH] Auth initialization failed:', error);
        // Continue anyway - user can still login
      } finally {
        if (isMounted) {
          setLoading(false);
          console.log('[AUTH] Auth initialization complete');
        }
      }
    };

    // Add safety timeout to ensure loading always completes
    // Reduced to 2 seconds since auth state change listener should fire quickly
    const timeout = setTimeout(() => {
      if (isMounted) {
        console.warn('[AUTH] Auth initialization timeout - forcing loading to false (auth state listener should have fired by now)');
        setLoading(false);
      }
    }, 2000); // 2 second safety timeout

    initAuth().then(() => {
      clearTimeout(timeout);
    }).catch((error) => {
      console.error('Auth initialization promise rejected:', error);
      clearTimeout(timeout);
      if (isMounted) {
        setLoading(false);
      }
    });

    // Listen for auth state changes - this is the primary way we detect login/logout
    // This fires immediately when auth state changes, including on page load
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AUTH] Auth state changed:', event, session?.user?.email || 'no user');
      
      if (session?.user) {
        try {
          // Set cookie for server-side auth check immediately
          // Use domain-less cookie so it works for all subpaths
          if (session.access_token) {
            try {
              const expires = new Date(session.expires_at * 1000).toUTCString();
              const cookieValue = `sb-access-token=${session.access_token}; expires=${expires}; path=/; SameSite=Lax; Secure`;
              document.cookie = cookieValue;
              console.log('[AUTH] Cookie set on auth state change');
            } catch (cookieError) {
              console.error('[AUTH] Error setting cookie:', cookieError);
            }
          }
          
          // Update user state immediately (don't wait for getOrCreateUser)
          if (isMounted) {
            setUserId(session.user.id);
            setUser(session.user);
            setLoading(false); // Clear loading state immediately when we have a session
            console.log('[AUTH] User state updated, loading cleared:', session.user.email);
          }
          
          // Ensure user exists in users table (non-blocking - do this in background)
          getOrCreateUser(session.user.id).catch((userError) => {
            console.warn('[AUTH] Error creating/getting user (non-blocking):', userError);
          });
        } catch (userError) {
          console.error('[AUTH] Error in auth state change handler:', userError);
          // Even on error, clear loading state
          if (isMounted) {
            setLoading(false);
          }
        }
      } else {
        // Clear cookie on logout
        document.cookie = 'sb-access-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        if (isMounted) {
          setUserId(null);
          setUser(null);
          // Only clear loading if this is an actual logout, not initial check
          if (event === 'SIGNED_OUT') {
            setLoading(false);
            console.log('[AUTH] User logged out, state cleared');
          } else if (event === 'INITIAL_SESSION') {
            // Don't clear loading on INITIAL_SESSION with no user - let initAuth handle it
            console.log('[AUTH] Initial session check - no active session');
          } else {
            setLoading(false);
            console.log('[AUTH] No active session');
          }
        }
      }
    });

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  return { userId, user, loading };
};
